/**
 * All API routes are in this file.
 */
import config, { Country } from 'config';
import { CmsItemNameType } from 'types/cms.types';
import urlJoin from 'url-join';

import { queryString } from '../utils/queryString';

const API_URL = config.api?.url;
const ADMIN_URL = config.admin?.url;
const WALLET_URL = config.wallet?.url;
const MESSAGE_URL = config.messaging?.url;

export const AUTH = {
  TOKEN: {
    REFRESH: {
      POST: () => `${API_URL}/auth/token/refresh`,
    },
    GUEST: {
      POST: () => `${API_URL}/auth/token/guest`,
    },
    USER: {
      POST: () => `${API_URL}/auth/token/nonce`,
    },
  },
  LOGIN: {
    FACEBOOK: {
      GET: (queryParams?) => `${API_URL}/auth/facebook/url/${queryString(queryParams)}`,
    },
    GOOGLE: {
      GET: (queryParams?) => `${API_URL}/auth/google/url/${queryString(queryParams)}`,
    },
  },
};

// REFERRAL
export const REFERRAL = {
  CODE: {
    GET: () => `${API_URL}/referral/code`,
  },
};

// USER
export const USER = {
  ONE: {
    GET: (userId: number | '' = '') => `${API_URL}/user/${userId}`,
  },
  UPDATE: {
    PATCH: () => `${API_URL}/user`,
    REFERRAL: {
      PATCH: () => `${API_URL}/user/referral`,
    },
  },
  UPDATE_CONTACT_DETAILS: {
    PATCH: () => `${API_URL}/user/contact-details`,
  },
  UPDATE_BANK_ACCOUNT: {
    PATCH: () => `${API_URL}/user/bank-account`,
  },
  UPDATE_EMAIL: {
    PATCH: () => `${API_URL}/user/email`,
  },
  UPDATE_CONSENT: {
    PATCH: () => `${API_URL}/user/consent`,
  },
  NOTIFICATION_PREFERENCES: {
    GET: () => `${API_URL}/user/notification-preferences`,
    PATCH: () => `${API_URL}/notification/preferences`,
  },
  ACCEPT_TERMS: {
    POST: () => `${API_URL}/user/terms-acceptance`,
  },

  BLOCK: (targetUserId: number) => `${API_URL}/user/block/${targetUserId}`,

  IMAGE: {
    POST: () => `${API_URL}/user/profile-picture`,
  },

  EMAIL: {
    VALIDATE: {
      GET: () => `${API_URL}/user/email-confirmed`,
    },
  },

  SHOP: {
    LIKE: {
      LIST: {
        GET: (queryParams?) => `${API_URL}/like/user/${queryString(queryParams)}`,
      },
      CREATE: {
        POST: (shopId: number) => `${API_URL}/like/shop/${shopId}`,
      },
      REMOVE: {
        DELETE: (shopId: number) => `${API_URL}/like/shop/${shopId}`,
      },
    },
  },

  LIKE: {
    LIST: {
      GET: (type: string, userId: number, queryParams?) => `${API_URL}/like/${type}/${userId}/likers/${queryString(queryParams)}`,
    },
    CREATE: {
      POST: (userId: number) => `${API_URL}/like/user/${userId}`,
    },
    REMOVE: {
      DELETE: (userId: number) => `${API_URL}/like/user/${userId}`,
    },
  },

  FEATURED: {
    GET: (queryParams?) => `${API_URL}/user/featured/${queryString(queryParams)}`,
  },

  LANGUAGE: {
    UPDATE: {
      PATCH: () => `${API_URL}/user/language`,
    },
  },

  CONTACT: {
    ONE: {
      GET: (queryParams) => `${MESSAGE_URL}/contact/${queryString(queryParams)}`,
    },
    UPDATE: {
      PATCH: (userId: number) => `${MESSAGE_URL}/contact/${userId}`,
    },
  },
  MONEY: {
    WITHDRAW: {
      POST: () => `${API_URL}/user/withdraw`,
    },
  },

  DELETE: {
    REASONS: {
      GET: () => `${API_URL}/user/reasons`,
    },
    DELETE: () => `${API_URL}/user`,
  },

  DAC7: {
    POST: () => `${API_URL}/user/dac7`,
  },

  PHONE: {
    VERIFIED: {
      GET: (phoneNumber?: string) => urlJoin(API_URL, 'user/phone', phoneNumber ?? '', 'verified'),
    },
  },
};

//BANNER
export const BANNER = {
  ACTIVE: {
    GET: () => `${API_URL}/cms/banner/active`,
  },
};

// CMS ITEM GET
/**
 * CMS endpoint returns a cms item's data by it's name,
 * or all cms items data if it's name is undefined
 * @param name
 * @returns {string} url
 */
export const COUNTRY = {
  LIST: {
    GET: () => `${API_URL}/country`,
  },
};

export const CMS_ITEM_URL = (name?: CmsItemNameType, queryParams?) => `${API_URL}/cms/${name || 'all'}${queryString(queryParams)}`;

// color
export const COLOR = {
  LIST: {
    GET: () => `${API_URL}/color`,
  },
};

// material
export const MATERIAL = {
  LIST: {
    GET: () => `${API_URL}/material`,
  },
};

// condition
export const CONDITION = {
  LIST: {
    GET: () => `${API_URL}/condition`,
  },
};

// DISCOUNT CODES (former VOUCHERS)
export const DISCOUNT_CODE = {
  LIST: {
    GET: (queryParams?) => `${API_URL}/voucher/${queryString(queryParams)}`,
  },
  ONE: {
    ID: {
      GET: (voucherId: number) => `${API_URL}/voucher/${voucherId}`,
    },
    CODE: {
      POST: (shopId: number, code: string) => `${API_URL}/voucher/${shopId}/${code}`,
    },
  },
  SAVE: {
    POST: () => `${API_URL}/voucher`,
  },
  REMOVE: {
    DELETE: (voucherId: number) => `${API_URL}/voucher/${voucherId}`,
  },
  CHANGE: {
    PATCH: (voucherId: number) => `${API_URL}/voucher/${voucherId}`,
  },
};

// DELIVERY
export const DELIVERY = {
  LOCATION: {
    GET: (deliveryServiceName: string, country?: Country, size?: string) => {
      const params = new URLSearchParams();

      if (country) {
        params.set('country', country);
      }

      if (size) {
        params.set('size', size);
      }

      const query = params.toString();

      return `${API_URL}/shipping/locations/${deliveryServiceName}${query ? `?${query}` : ''}`;
    },
  },
  ADDRESS_PICKUP_POINTS: {
    GET: (methods: string[], size?: string) => {
      const params = new URLSearchParams({ methods: methods.join(',') });

      if (size) {
        params.set('size', size);
      }

      return `${API_URL}/shipping/address-pickup-points?${params.toString()}`;
    },
  },
  OPTIONS: {
    GET: () => `${API_URL}/shipping/options`,
    PARGO: {
      LOCATION: { GET: (locationId: string) => `${API_URL}/shipping/options/pargo/locations/${locationId}` },
    },
    MTAANI: {
      PRICE: {
        GET: (sid: number, rid: number, productPrice: number) =>
          `${API_URL}/shipping/options/mtaani/price?senderAgentId=${sid}&receiverAgentId=${rid}&productPrice=${productPrice}`,
      },
      PRICE_INFO: {
        GET: (sid: number, rid: number, productPrice: number) =>
          `${API_URL}/shipping/options/mtaani/price-info?senderAgentId=${sid}&receiverAgentId=${rid}&productPrice=${productPrice}`,
      },
      AREAS: { GET: () => `${API_URL}/shipping/options/mtaani/areas` },
      AREA: { GET: (areaId: number) => `${API_URL}/shipping/options/mtaani/areas/${areaId}` },
      LOCATIONS: { GET: (areaId: number) => `${API_URL}/shipping/options/mtaani/locations?areaId=${areaId}` },
      LOCATION: { GET: (locationId: number) => `${API_URL}/shipping/options/mtaani/locations/${locationId}` },
      AGENTS: { GET: (locationId: number) => `${API_URL}/shipping/options/mtaani/agents?locationId=${locationId}` },
      NAIROBICBDAGENTS: { GET: () => `${API_URL}/shipping/options/mtaani/agents?location=nairobi-cbd` },
    },
  },
  PRICE: {
    POST: (deliveryServiceName: string) => `${API_URL}/shipping/${deliveryServiceName}/price`,
  },
  FREE_OPTIONS: {
    GET: () => `${API_URL}/shipping/free-options`,
  },
};

// IMAGE
export const IMAGE = {
  UPLOAD: {
    POST: () => `${API_URL}/image`,
  },
  INFO: {
    GET: (queryParams) => `${API_URL}/image/info/${queryString(queryParams)}`,
  },
};

// PRODUCT
export const PRODUCT = {
  LIST: {
    GET: (queryParams?) => `${API_URL}/product/${queryString(queryParams)}`,
  },
  HOME_FEED: {
    GET: (queryParams?) => `${API_URL}/product/home-feed${queryString(queryParams)}`,
  },
  RECOMMENDATION: {
    GET: (productSlug, queryParams?) => `${API_URL}/product/${productSlug}/recommendations${queryString(queryParams)}`,
  },
  FILTER: {
    GET_HOMEPAGE: () => `${API_URL}/cms/homepageProducts`,
  },
  ONE: {
    GET: (slug, queryParams?) => `${API_URL}/product/${slug}/${queryString(queryParams)}`,
    GET_BUNDLING_AVALIABILITY: (slug, queryParams?) => `${API_URL}/product/bundling/${slug}/${queryString(queryParams)}`,
  },
  UPLOAD_URL: {
    GET: (queryParams?) => `${API_URL}/product/uploadurl/${queryString(queryParams)}`,
  },

  CREATE: {
    POST: () => `${API_URL}/product`,
  },
  UPDATE: {
    PATCH: (productId) => `${API_URL}/product/${productId}`,
    BY_SLUG: {
      PATCH: (productSlug) => `${API_URL}/product/${productSlug}`,
    },
  },
  REMOVE: {
    DELETE: (slug) => `${API_URL}/product/${slug}`,
  },
  ACCEPTED_OFFERS: {
    CANCEL: {
      POST: (productId: number) => `${API_URL}/product/${productId}/offers/cancel-accepted`,
    },
  },

  FEATURED: {
    GET: (queryParams?) => `${API_URL}/product/featured/${queryString(queryParams)}`,
  },

  SEARCH: {
    GET: (queryParams?) => `${API_URL}/product/search/${queryString(queryParams)}`,
  },

  IMAGE: {
    UPLOAD: {
      POST: (productId: number) => `${API_URL}/product/${productId}/image`,
    },
    REMOVE: {
      DELETE: (productId: number, imageId: number) => `${API_URL}/product/${productId}/image/${imageId}`,
    },
  },
  IMAGES: {
    UPLOAD: {
      POST: (productId: number) => `${API_URL}/product/${productId}/images`,
    },
  },

  LIKE: {
    LIST: {
      GET: (queryParams?) => `${API_URL}/like/product/${queryString(queryParams)}`,
    },
    CREATE: {
      POST: (productId) => `${API_URL}/like/product/${productId}`,
    },
    REMOVE: {
      DELETE: (productId, queryParams?) => `${API_URL}/like/product/${productId}${queryString(queryParams)}`,
    },
  },

  TAG: {
    GET: () => `${API_URL}/product/tags`,
  },

  SIZE: {
    GET: (categoryId?: number, queryParams?) => urlJoin(API_URL, 'product/sizes', categoryId?.toString() ?? '', queryString(queryParams)),
    FILTER: {
      GET: (categoryId?: number, queryParams?) =>
        urlJoin(API_URL, 'product/filter/sizes', categoryId?.toString() ?? '', queryString(queryParams)),
    },
  },

  CAMPAIGNS: {
    GET: () => `${API_URL}/cms/product-campaigns`,
  },

  ADD_PIN: {
    POST: (productId) => `${ADMIN_URL}/product/${productId}/pin`,
  },

  REMOVE_PIN: {
    DELETE: (productId) => `${ADMIN_URL}/product/${productId}/pin`,
  },

  HIDE_PRODUCT: {
    POST: (productId) => `${ADMIN_URL}/product/${productId}/toggle-hide`,
  },

  DELETE_PRODUCT: {
    DELETE: (productId) => `${ADMIN_URL}/product/${productId}`,
  },
  DELETE_REASONS: {
    GET: () => `${ADMIN_URL}/product/delete-reasons`,
  },
  ORDER_SUBTOTALS: {
    GET: (productId: string, discountCode?: string) => {
      return `${API_URL}/product/${productId}/discounted-sub-totals${discountCode ? `?voucher_code=${discountCode}` : ''}`;
    },
  },
  HIDE_REASONS: {
    GET: () => `${ADMIN_URL}/product/hide-reasons`,
  },
  SEARCH_METRICS: {
    POST: () => `${API_URL}/product/search-metrics`,
  },

  AI_PREFILL: {
    POST: () => `${API_URL}/product/prefill`,
  },
};

// MESSAGES
export const MESSAGE = {
  COUNT: {
    GET: () => `${MESSAGE_URL}/message/unread`,
  },
  LIST: {
    GET: (queryParams?) => `${MESSAGE_URL}/message/people/${queryString(queryParams)}`,
  },
  ONE: {
    GET: (queryParams?) => `${MESSAGE_URL}/message/${queryString(queryParams)}`,
  },
  SEND: {
    POST: () => `${MESSAGE_URL}/message`,
  },
  READ: {
    PATCH: () => `${MESSAGE_URL}/message/read`,

    ALL: {
      PATCH: () => `${MESSAGE_URL}/message/read/all`,
    },
  },
  TRANSLATION: {
    ENABLE: {
      POST: () => `${MESSAGE_URL}/message/translation/enable`,
    },
    DISABLE: {
      POST: () => `${MESSAGE_URL}/message/translation/disable`,
    },
    STATUS: {
      GET: () => `${MESSAGE_URL}/message/translation/status`,
    },
  },
};

// NOTIFICATIONS
export const NOTIFICATIONS = {
  COUNT: {
    GET: () => `${MESSAGE_URL}/message/unread?type=system`,
  },
  LIST: {
    GET: () => `${MESSAGE_URL}/message?userId=system`,
  },
  ALL_COUNT: {
    GET: () => `${MESSAGE_URL}/message/unread/all?noTimeoutHandling=true`,
  },
  READ: {
    PATCH: () => `${MESSAGE_URL}/message/read`,
  },
};

// ORDER
export const ORDER = {
  LIST: {
    GET: (queryParams?) => `${API_URL}/order/${queryString(queryParams)}`,
  },
  ONE: {
    GET: (orderId: number) => `${API_URL}/order/${orderId}`,
  },
  SUBMIT: {
    POST: () => `${API_URL}/order`,
  },
  UPDATE: {
    BARCODE: {
      PATCH: (orderId: number) => `${API_URL}/order/${orderId}/barcode`,
    },
    DOOR_CODE: {
      POST: (orderId: number) => `${API_URL}/order/${orderId}/doorcode`,
    },
    PATCH: (orderId: number) => `${API_URL}/order/${orderId}`,
  },
  SUBTOTALS: {
    POST: () => `${API_URL}/order/discounted-sub-totals`, //TODO: remove when GET is approved
    GET: (productId: string, voucherCode?: string) => {
      return `${API_URL}/order/${productId}/discounted-sub-totals${voucherCode ? `?voucher_code=${voucherCode}` : ''}`;
    },
  },
};

// SHOP
export const SHOP = {
  ONE: {
    GET: (slug: string) => `${API_URL}/shop/${slug}`,
  },
  UPDATE: {
    PATCH: (shopId: number) => `${API_URL}/shop/${shopId}`,
  },
  LIST: {
    CATEGORY: {
      GET: (category, queryParams?) => `${API_URL}/shop/category/${category}/${queryString(queryParams)}`,
    },
  },

  SLUG: {
    UPDATE: {
      PATCH: (shopId: number) => `${API_URL}/shop/${shopId}/slug`,
    },
    GET: (shopName: string) => `${API_URL}/shop/slug${queryString({ shopName })}`,
  },

  SEARCH: {
    GET: (queryParams?) => `${API_URL}/shop/search/${queryString(queryParams)}`,
  },

  FEATURED: {
    GET: () => `${API_URL}/cms/featured-shop`,
  },

  FEATURED_BRANDS: {
    GET: () => `${API_URL}/cms/featured-links`,
  },

  TOGGLE_APPROPRIATE_PRODUCTS: {
    PATCH: (shopId) => `${ADMIN_URL}/shop/${shopId}`,
  },

  FEEDBACKS: {
    LIST: {
      GET: (shopId: number, queryParams?) => `${API_URL}/feedback/${shopId}${queryString(queryParams)}`,
    },
    POST: (shopId: number) => `${API_URL}/feedback/${shopId}`,
    PATCH: (feedbackId: number) => `${API_URL}/feedback/${feedbackId}`,
    DELETE: (feedbackId: number) => `${API_URL}/feedback/${feedbackId}`,

    REPLY: {
      POST: (feedbackId: number) => `${API_URL}/feedback/reply/${feedbackId}`,
      DELETE: (feedbackId: number) => `${API_URL}/feedback/reply/${feedbackId}`,
    },
  },
};

// WALLET
export const WALLET = {
  ACCOUNT: {
    GET: () => `${WALLET_URL}/account/self`,
    SHOPPING_BALANCE: {
      GET: () => `${WALLET_URL}/account/self/shopping-balance`,
    },
  },
  PAYMENT: {
    GET: {
      PEACHPAYMENTS_CC: (transactionId) => `${WALLET_URL}/payment/peachpayments/cc/status/${transactionId}`,
      PEACHPAYMENTS_EMBEDDED: (checkoutId) => `${WALLET_URL}/payment/peachpayments/embedded/status/${checkoutId}`,
      PEACHPAYMENTS_EMBEDDED_EFT: (merchantTransactionId) =>
        `${WALLET_URL}/payment/peachpayments/embeddedeft/status/${merchantTransactionId}`,
      MPESA: (transactionId) => `${WALLET_URL}/payment/mpesa/status/${transactionId}`,
      PAYMOB: (transactionId) => `${WALLET_URL}/payment/paymob/status/${transactionId}`,
      MOBICRED: (transactionId) => `${WALLET_URL}/payment/mobicred/status/${transactionId}`,
      MONTONIO: (paymentToken) => `${WALLET_URL}/payment/montonio/status/${paymentToken}`,
    },
  },
  TRANSACTION: {
    GET: (queryParams?) => `${WALLET_URL}/account/self/transaction/${queryString(queryParams)}`,
  },
  ORDER: {
    CONFIRM: {
      MAKSEKESKUS: {
        POST: () => `${WALLET_URL}/webhook/maksekeskus`,
      },
      MONTONIO: {
        POST: () => `${WALLET_URL}/webhook/montonio`,
      },
      OZOW: {
        POST: () => `${WALLET_URL}/webhook/ozowEFT`,
      },
    },
  },
};

// CATEGORIES
export const CATEGORIES = {
  LIST: {
    GET_CMS: () => `${API_URL}/cms/categories`,
    GET: (queryParams?) => `${API_URL}/category${queryString(queryParams)}`,
  },
};

// BRAND
export const BRAND = {
  LIST: {
    GET: (queryParams?) => `${API_URL}/brand/${queryString(queryParams)}`,
  },
  CMS_ITEM: {
    GET: (queryParams) => `${API_URL}/cms/brands/${queryString(queryParams)}`,
  },
};

// REPORT
export const REPORT = {
  LIST: {
    REASON: {
      GET: () => `${API_URL}/report/reasons`,
    },
  },
  CREATE: {
    POST: () => `${API_URL}/report`,
  },
};

// OFFER
export const OFFER = {
  ONE: {
    GET: (offerId: number) => `${API_URL}/offer/${offerId}`,
    ACTIVE: {
      GET: (productId: number) => `${API_URL}/offer/product/${productId}`,
    },
  },
  CREATE: {
    POST: () => `${API_URL}/offer`,
  },
  COUNTER: {
    POST: () => `${API_URL}/offer/counter`,
  },
  SELLER: {
    POST: () => `${API_URL}/offer/seller`,
  },
  UPDATE: {
    PATCH: () => `${API_URL}/offer`,
  },
};

export const USER_SAVED_SEARCH = {
  LIST: {
    GET: (id?: number, queryParams?) => `${API_URL}/saved-search/${id || ''}${queryString(queryParams)}`,
  },
  CREATE: {
    POST: () => `${API_URL}/saved-search`,
  },
  DELETE: {
    DELETE: (id: number) => `${API_URL}/saved-search/${id}`,
  },
};

export const OTP = {
  SEND: {
    POST: () => `${API_URL}/otp/send`,
  },
  VERIFY: {
    POST: () => `${API_URL}/otp/verify`,
  },
};

export const TRANSLATION = {
  ALL: {
    GET: () => `${API_URL}/translation`,
  },
};

// CATEGORIES
export const ANNOUNCEMENT = {
  HIDE: {
    PATCH: () => `${API_URL}/announcement/hide`,
  },
};

// RECSYS TRACKING
export const TRACK = {
  SEARCH: {
    POST: () => `${API_URL}/track/search`,
  },
  PRODUCT_VIEW: {
    POST: () => `${API_URL}/track/product-view`,
  },
  PRODUCT_SELECT: {
    POST: () => `${API_URL}/track/product-select`,
  },
};
